/**
 * Centralized breadcrumb (crumb) functions for all routes
 * Follows the same pattern as loaders - each route's breadcrumb logic is isolated
 */

import { IApiService, BailleurItem } from './services/ApiService';
import { MainRouteData, BailleurPageData, LogementPageData } from './routeFactory';

const bailleursLink = {
    path: "/bailleurs",
    title: "Bailleurs"
};

// Main route breadcrumb
export const mainCrumb = (path: string, _: MainRouteData) => [{
    path: path,
    title: "Diagnostic Accessibilité"
}];

// Account - Login
export const loginCrumb = () => [{
    path: "/account/login",
    title: "Connexion"
}];

// Account - Forgot Password
export const forgotPasswordCrumb = () => [{
    path: "/account/forgot-password",
    title: "Mot de passe perdu"
}];

// Account - Reset Password
export const resetPasswordCrumb = () => [{
    path: "/account/reset-password",
    title: "Réinitialiser le mot de passe"
}];

// Account - Me (Profile)
export const meCrumb = () => [{
    path: "/account/me",
    title: "Mon compte"
}];

// Bailleurs page
export const bailleursPageCrumb = (apiService: IApiService) => (_path: string, _: MainRouteData) => {
    if (apiService.isCurrentUserBailleur()) {
        return [];
    } else {
        return [bailleursLink];
    }
};

// Bailleur page (/:bailleurId)
export const bailleurCrumb = (apiService: IApiService) => (path: string, data: BailleurPageData) => {
    const bailleurName = data.bailleur?.nom ?? (data.bailleur === null ? "Hors bailleur" : "???");
    const bailleurLink = {
        path: path,
        title: apiService.isCurrentUserBailleur() ? "Programmes" : bailleurName
    };
    if (apiService.isCurrentUserBailleur()) {
        return [bailleurLink];
    } else {
        return [bailleursLink, bailleurLink];
    }
};

// Logements page (under bailleur)
export const logementsCrumb = (path: string, data: BailleurPageData & { programme: string | null }) => {
    return [{
        path: path,
        title: data.programme ?? "Hors programme"
    }];
};

// New logement page
export const newLogementCrumb = (apiService: IApiService) => (path: string, data: LogementPageData) => {
    if (data.programme === undefined) {
        throw new Error("Unexpected");
    }
    if (!data.logement) {
        throw new Error("Unexpected");
    }
    if (!data.bailleurs) {
        throw new Error("Unexpected");
    }

    // Crumb pour le bailleur
    const bailleurId = data.logement.bailleur;
    const bailleurLabel = apiService.isCurrentUserBailleur() ? "Programmes" : bailleurId ? data.bailleurs?.find((x: BailleurItem) => x.id === bailleurId)?.nom : undefined;
    const bailleurLink = {
        path: `/${bailleurId ?? 0}`,
        title: bailleurLabel ?? "Hors bailleur"
    };

    // Crumb pour le programme
    const programmeLink = {
        path: `${bailleurId ?? 0}/logements/${data.programme ? encodeURIComponent(data.programme) : ""}`,
        title: data.programme ?? "Hors programme"
    };

    // Crumb pour le logement
    const logementLink = {
        path: path,
        title: "Nouveau logement"
    };

    const r = [];
    if (data.forceBailleur) {
        r.push(bailleurLink);
    }
    if (data.forceProgramme) {
        r.push(programmeLink);
    }
    r.push(logementLink);
    return r;
};

// View logement page
export const viewLogementCrumb = (apiService: IApiService) => (path: string, data: LogementPageData) => {
    if (data.programme === undefined) {
        throw new Error("Unexpected");
    }
    if (!data.logement) {
        throw new Error("Unexpected");
    }
    if (!data.bailleurs) {
        throw new Error("Unexpected");
    }

    // Crumb pour le bailleur
    const bailleurId = data.logement.bailleur;
    const bailleurLabel = apiService.isCurrentUserBailleur() ? "Programmes" : bailleurId ? data.bailleurs?.find((x: BailleurItem) => x.id === bailleurId)?.nom : undefined;
    const bailleurLink = {
        path: `/${bailleurId ?? 0}`,
        title: bailleurLabel ?? "Hors bailleur"
    };

    // Crumb pour le programme
    const programmeLink = {
        path: `${bailleurId ?? 0}/logements/${data.programme ? encodeURIComponent(data.programme) : ""}`,
        title: data.programme ?? "Hors programme"
    };

    // Crumb pour le logement
    const logementLink = {
        path: path,
        title: `Logement N°${data.logement.numLogement ?? "???"}`
    };

    if (apiService.isCurrentUserBailleur()) {
        return [bailleurLink, programmeLink, logementLink];
    } else {
        return [bailleursLink, bailleurLink, programmeLink, logementLink];
    }
};

// Edit logement page
export const editLogementCrumb = (apiService: IApiService) => (path: string, data: LogementPageData) => {
    if (data.programme === undefined) {
        throw new Error("Unexpected");
    }
    if (!data.logement) {
        throw new Error("Unexpected");
    }
    if (!data.bailleurs) {
        throw new Error("Unexpected");
    }

    // Crumb pour le bailleur
    const bailleurId = data.logement.bailleur;
    const bailleurLabel = apiService.isCurrentUserBailleur() ? "Programmes" : bailleurId ? data.bailleurs?.find((x: BailleurItem) => x.id === bailleurId)?.nom : undefined;
    const bailleurLink = {
        path: `/${bailleurId ?? 0}`,
        title: bailleurLabel ?? "Hors bailleur"
    };

    // Crumb pour le programme
    const programmeLink = {
        path: `${bailleurId ?? 0}/logements/${data.programme ? encodeURIComponent(data.programme) : ""}`,
        title: data.programme ?? "Hors programme"
    };

    // Crumb pour le logement
    const logementLink = {
        path: path,
        title: "Mise à jour"
    };

    if (apiService.isCurrentUserBailleur()) {
        return [bailleurLink, programmeLink, logementLink];
    } else {
        return [bailleursLink, bailleurLink, programmeLink, logementLink];
    }
};
